import { useEffect, useRef } from "react";
import type { ControllerRenderProps } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PricingMode } from "@/gql/graphql";
import { CenteredSpinner } from "@/lib/components/CenteredSpinner";
import { useSalonStore } from "@/lib/store/salon";
import { getSalonCurrencySymbol } from "@/lib/utils/currency";
import type { ServiceEditFormData } from "../../validation/service-edit-schema";
import { ServiceMatrixOptionsForm } from "./service-matrix-options-form";

// Extracted component so hooks can be called at the top level
function BasePriceInput({
  field,
  currencySymbol,
  pricingMode,
  saving,
}: {
  field: ControllerRenderProps<ServiceEditFormData, "pricing.basePrice">;
  currencySymbol: string;
  pricingMode: string;
  saving: boolean;
}) {
  const { t } = useTranslation("services");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && field.value !== undefined) {
      const displayValue = field.value > 0 ? (field.value / 100).toFixed(2) : "";
      if (inputRef.current.value !== displayValue) {
        inputRef.current.value = displayValue;
      }
    }
  }, [field.value]);

  return (
    <FormItem>
      <FormLabel>
        {pricingMode === "SINGLE_PRICE"
          ? t("servicePricingForm.fields.servicePrice")
          : t("servicePricingForm.fields.basePrice")}
      </FormLabel>
      <FormControl>
        <div className="relative max-w-32">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <span className="text-muted-foreground text-sm font-medium">{currencySymbol}</span>
          </div>
          <Input
            ref={inputRef}
            type="text"
            placeholder="0.00"
            className="pl-10 text-right"
            defaultValue={field.value > 0 ? (field.value / 100).toFixed(2) : ""}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value === "" || value === "0" || value === "0.00") {
                field.onChange(0);
                e.target.value = "";
              } else {
                const dollars = Number.parseFloat(value);
                if (!Number.isNaN(dollars) && dollars >= 0 && dollars <= 9999.99) {
                  field.onChange(Math.round(dollars * 100));
                  e.target.value = dollars.toFixed(2);
                } else {
                  const previousValue = field.value > 0 ? (field.value / 100).toFixed(2) : "";
                  e.target.value = previousValue;
                }
              }
            }}
            onKeyDown={(e) => {
              if (
                [8, 9, 27, 13, 46, 110, 190].indexOf(e.keyCode) !== -1 ||
                (e.keyCode === 65 && e.ctrlKey === true) ||
                (e.keyCode === 67 && e.ctrlKey === true) ||
                (e.keyCode === 86 && e.ctrlKey === true) ||
                (e.keyCode === 88 && e.ctrlKey === true)
              ) {
                return;
              }
              if (
                (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
                (e.keyCode < 96 || e.keyCode > 105)
              ) {
                e.preventDefault();
              }
            }}
            disabled={saving}
          />
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  );
}

interface ServicePricingFormProps {
  loading?: boolean;
  saving?: boolean;
}

export function ServicePricingForm({ loading = false, saving = false }: ServicePricingFormProps) {
  const { t } = useTranslation("services");
  const { control } = useFormContext<ServiceEditFormData>();
  const currency = useSalonStore((state) => state.currentSalon?.currency);

  // Get currency symbol using built-in Intl API
  const currencySymbol = getSalonCurrencySymbol(currency);

  const pricingMode = useWatch({ control, name: "pricing.pricingMode" });
  const _basePrice = useWatch({ control, name: "pricing.basePrice" });

  // Helper function to get localized pricing mode label
  const getPricingModeLabel = (mode: PricingMode) => {
    switch (mode) {
      case "NO_PRICE":
        return t("servicePricingForm.pricingOptions.noPrice");
      case "SINGLE_PRICE":
        return t("servicePricingForm.pricingOptions.fixedPrice");
      case "MATRIX_PRICE":
        return t("servicePricingForm.pricingOptions.configurablePrice");
      default:
        return t("servicePricingForm.placeholders.pricingMode");
    }
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-0">
          <CenteredSpinner minHeight="min-h-[200px]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t("servicePricingForm.title")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pricing Mode Selection */}
          <FormField
            control={control}
            name="pricing.pricingMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("servicePricingForm.fields.pricingMode")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={saving}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue>
                        {field.value
                          ? getPricingModeLabel(field.value)
                          : t("servicePricingForm.placeholders.pricingMode")}
                      </SelectValue>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="NO_PRICE">
                      {t("servicePricingForm.pricingOptions.noPrice")}
                    </SelectItem>
                    <SelectItem value="SINGLE_PRICE">
                      {t("servicePricingForm.pricingOptions.fixedPrice")}
                    </SelectItem>
                    <SelectItem value="MATRIX_PRICE">
                      {t("servicePricingForm.pricingOptions.configurablePrice")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Base Price Input */}
          {(pricingMode === "SINGLE_PRICE" || pricingMode === "MATRIX_PRICE") && (
            <FormField
              control={control}
              name="pricing.basePrice"
              render={({ field }) => (
                <BasePriceInput
                  field={field}
                  currencySymbol={currencySymbol}
                  pricingMode={pricingMode}
                  saving={saving}
                />
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Matrix Pricing Options - Show when pricing mode is MATRIX_PRICE */}
      <ServiceMatrixOptionsForm showInlineTemplates={false} />
    </>
  );
}
